import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSocket } from "../context/SocketContext";
import toastError from "../errors/toastError";
import api, { markUserAsInactive, markRefreshTime } from "../services/api";

const useAuth = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isAuth, setIsAuth] = useState(false);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState({});
    const [impersonatedUser, setImpersonatedUser] = useState(() => {
        try { const s = localStorage.getItem("impersonatedUser"); return s ? JSON.parse(s) : null; } catch { return null; }
    });
    const { socket } = useSocket();

    const handleLogout = useCallback(async () => {
        try {
            await api.delete("/auth/logout");
        } catch (err) {
            console.error("Erro ao fazer logout:", err);
        }
        
        if (socket) {
            socket.emit("logout");
            socket.disconnect();
        }
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        delete api.defaults.headers.Authorization;
        setIsAuth(false);
        setUser({});
        navigate("/login");
    }, [navigate, socket]);

    const [userInactive, setUserInactive] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            if (userInactive) {
                setLoading(false);
                return;
            }

            const tokenStr = localStorage.getItem("token");
            if (tokenStr) {
                try {
                    const token = JSON.parse(tokenStr);
                    api.defaults.headers.Authorization = `Bearer ${token}`;
                    
                    try {
                        const userStr = localStorage.getItem("user");
                        if (userStr) {
                            const storedUser = JSON.parse(userStr);
                            setUser(storedUser);
                            setIsAuth(true);
                        }
                    } catch (parseErr) {
                        console.error("Erro ao carregar usuário do localStorage:", parseErr);
                    }

                    const { data } = await api.post("/auth/refresh_token");
                    api.defaults.headers.Authorization = `Bearer ${data.token}`;
                    markRefreshTime();
                    localStorage.setItem("token", JSON.stringify(data.token));
                    localStorage.setItem("user", JSON.stringify(data.user));
                    setIsAuth(true);
                    setUser(data.user);
                } catch (err) {
                    if (err.response && err.response.data && err.response.data.error === "ERR_USER_INACTIVE") {
                        setUserInactive(true);
                        markUserAsInactive();

                        toast.error(t("backendErrors.ERR_USER_INACTIVE") || "Este atendente está desativado!", {
                            autoClose: 10000,
                            onClose: () => {
                                navigate("/login");
                            }
                        });

                        localStorage.removeItem("token");
                        localStorage.removeItem("user");
                        setIsAuth(false);
                        setUser({});

                        delete api.defaults.headers.Authorization;
                    } else {
                        // Limpa o estado de auth SINCRONAMENTE antes de setLoading(false)
                        // para evitar que PublicRoute veja isAuth=true enquanto loading=false
                        // e redirecione para home antes do handleLogout terminar (era o bug do "login recarrega")
                        localStorage.removeItem("token");
                        localStorage.removeItem("user");
                        delete api.defaults.headers.Authorization;
                        setIsAuth(false);
                        setUser({});
                        // Chama logout no servidor em background (fire-and-forget)
                        api.delete("/auth/logout").catch(() => {});
                    }
                }
            }
            setLoading(false);
        };

        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogin = async (userData) => {
        try {
            const { data } = await api.post("/auth/login", userData);
            localStorage.setItem("token", JSON.stringify(data.token));
            localStorage.setItem("user", JSON.stringify(data.user));
            localStorage.setItem("theme", data.user.theme);
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            markRefreshTime();
            setUser(data.user);
            setIsAuth(true);
            toast.success(t("auth.toasts.success"));
        } catch (err) {
            throw err;
        }
    };

    const handleImpersonate = async (userId) => {
        try {
            const { data } = await api.post("/auth/impersonate", { userId });
            // Salva sessão original
            localStorage.setItem("adminToken", localStorage.getItem("token"));
            localStorage.setItem("adminUser", localStorage.getItem("user"));
            // Aplica sessão do atendente
            localStorage.setItem("token", JSON.stringify(data.token));
            localStorage.setItem("user", JSON.stringify(data.user));
            localStorage.setItem("impersonatedUser", JSON.stringify(data.user));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            setUser(data.user);
            setImpersonatedUser(data.user);
            navigate("/tickets");
        } catch (err) {
            toastError(err, t);
        }
    };

    const handleExitImpersonation = useCallback(() => {
        const adminToken = localStorage.getItem("adminToken");
        const adminUser = localStorage.getItem("adminUser");
        if (!adminToken || !adminUser) return;
        localStorage.setItem("token", adminToken);
        localStorage.setItem("user", adminUser);
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        localStorage.removeItem("impersonatedUser");
        const parsedToken = JSON.parse(adminToken);
        api.defaults.headers.Authorization = `Bearer ${parsedToken}`;
        setUser(JSON.parse(adminUser));
        setImpersonatedUser(null);
        navigate("/users");
    }, [navigate]);

    return { isAuth, user, loading, handleLogin, handleLogout, handleImpersonate, handleExitImpersonation, impersonatedUser };
};

export default useAuth;
